import { ReactNode, useEffect, useCallback } from 'react';
import { useOutletContext } from "react-router-dom";

import LeftMenu from "~/components/LeftMenu";
import ContentLayout from "~/components/ContentLayout";

import LeaveModal from "~/modals/LeaveModal";
import Notify from "~/components/Notify";
import type { ITemplateUser, IOutlet } from "~/interfaces";
import { userStore } from "~/stores/user";
import { notificationStore } from "~/stores/notification";
import { classNames } from '~/utils/utils';
import { setSEO } from '~/utils/seo';
import { useBlocker } from '~/hooks/usePrompt';
import { myUseState } from "~/hooks/myUseState";
import { Portal } from '@mui/base';

import './styles.less';

interface IState {
	leaveCb: () => void;
}

interface IPageProps extends ITemplateUser {
	children: ReactNode;
	fullscreen?: boolean;
	onLoad?: (isAuthorized: boolean) => void;
	isDirty?: boolean;
}

export default function Page({
	children,
	fullscreen = true,
	title = "",
	seoTitleTrans = "seo.title.main",
	checkLogged = false,
	checkInternalRole = "none",
	breadcrumbs = [],
	onLoad = () => {},
	isDirty = false,
}: IPageProps) {
	const { userData } = userStore(userState => ({
		userData: userState.user,
	}));
	const { notification, clearNotification } = notificationStore(notificationState => ({
		notification: notificationState.notification,
		clearNotification: notificationState.clearNotification,
	}));
	const { state, updateState } = myUseState<IState>({
		leaveCb: null,
	});
	const { params }: IOutlet = useOutletContext();
	const isLogged = userData.email !== "";
	const checkInternalUser = checkInternalRole !== "none";

	function checkInternalUserRole(): boolean {
		if (!isLogged) {
			return false;
		}

		if (checkInternalRole === "salesman" && userData.internalRole !== "none") {
			return true;
		}

		if (checkInternalRole === "internal_admin" && userData.internalRole === "internal_admin") {
			return true;
		}

		return false;
	}

	function onLeaveSave() {
		state.leaveCb();
		updateState({
			leaveCb: null,
		});
	}

	const isAuthorized = (checkLogged && isLogged) || (checkInternalUser && checkInternalUserRole());
	const blocker = useCallback((txCont: { retry: () => void }) => {
		updateState({ leaveCb: () => txCont.retry() });
	}, []);

	// blokovani stranky
	useBlocker(blocker, isDirty);
	// pri nacteni stranky smazeme starou notifikaci
	useEffect(() => {
		clearNotification();
		setSEO(seoTitleTrans);
	}, []);

	useEffect(() => {
		onLoad(isAuthorized);
	}, [userData.email]);

	return <div className={classNames(["page", ...fullscreen ? ["fullscreen-page"] : []])}>
		<LeftMenu projectId={params.projectId} keyId={params.keyId} checkInternalRole={checkInternalRole} />
		<ContentLayout projectId={params.projectId} keyId={params.keyId} title={title} breadcrumbs={breadcrumbs} checkInternalRole={checkInternalRole} showGlobalStripe={false} isAuthorized={isAuthorized}>
			{ children }
		</ContentLayout>
		<Portal>
			<Notify open={ notification.visible } onClose={() => clearNotification()} type={ notification.color }>
				<p style={{ whiteSpace: "pre-line" }}>
					{ notification.msg }
				</p>
			</Notify>
		</Portal>
		{ state.leaveCb && <LeaveModal onClose={() => updateState({ leaveCb: null })} onSave={onLeaveSave} /> }
	</div>;
}
